import { useState, useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, FileText, ExternalLink, Copy, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { TiptapEditor } from "./TiptapEditor";
import OverlayInstanceSelectors from "./OverlayInstanceSelectors";
import ColorPickerInput from "@/components/ui/ColorPickerInput";
import HeadingLevelSelect from "@/components/ui/HeadingLevelSelect";
import { resolveFontSize, DEFAULT_HEADING_LEVEL } from "@/utils/headingSize";
import useAutoSave from "@/hooks/useAutoSave";
import { AutoSaveStatus } from "@/components/ui/AutoSaveStatus";

const API = getApiUrl();

// Curated Google Font choices for the Page Title control. Matches the
// list used by Site Styles → headingFont so the "Site default" option
// blends seamlessly.
const TITLE_FONTS = [
  { label: "Site default", value: "" },
  { label: "Inter", value: "Inter" },
  { label: "Roboto", value: "Roboto" },
  { label: "Poppins", value: "Poppins" },
  { label: "Raleway", value: "Raleway" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Nunito", value: "Nunito" },
  { label: "DM Sans", value: "DM Sans" },
  { label: "Plus Jakarta Sans", value: "Plus Jakarta Sans" },
  { label: "Space Grotesk", value: "Space Grotesk" },
  { label: "Playfair Display", value: "Playfair Display" },
  { label: "Merriweather", value: "Merriweather" },
  { label: "Lora", value: "Lora" },
  { label: "Cormorant Garamond", value: "Cormorant Garamond" },
  { label: "EB Garamond", value: "EB Garamond" },
  { label: "Outfit", value: "Outfit" },
  { label: "Syne", value: "Syne" },
  { label: "Figtree", value: "Figtree" },
];

// Empty title_style means "follow the admin Site Styles defaults" — no
// per-page overrides are applied at render time. `fontSize` is a heading
// level ("h1"–"h6"); blank → DEFAULT_HEADING_LEVEL ("h3") via Site Styles.
const EMPTY_TITLE_STYLE = {
  fontFamily: "",
  fontSize: DEFAULT_HEADING_LEVEL,
  fontWeight: "",
  color: "",
  textAlign: "",
};

export function CustomStaticPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPage, setPreviewPage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [pageForm, setPageForm] = useState({
    title: "",
    slug: "",
    content: "",
    enabled: true,
    requires_agent_login: false
  });
  // Overlay instances (max 2 each) shown before/after the page's main content.
  const [overlays, setOverlays] = useState({ pre_instances: [], post_instances: [] });

  useEffect(() => {
    fetchPages();
  }, []);

  // Lazy-load the Google Font chosen for the Page Title preview so admins
  // can see the actual face inside the dialog without leaving the page.
  useEffect(() => {
    const family = pageForm?.title_style?.fontFamily;
    if (!family) return;
    const id = `slh-admin-title-font-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, "+")}:wght@300;400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
  }, [pageForm?.title_style?.fontFamily]);

  const fetchPages = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/admin/custom-static-pages`, {
        withCredentials: true
      });
      setPages(response.data.pages || []);
    } catch (error) {
      console.error("Failed to fetch pages:", error);
      if (!silent) toast.error("Failed to load pages");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Inline card toggles — flip a single flag and PUT immediately (backend
  // now merges partial updates, see update_custom_static_page) so this
  // can't accidentally wipe the page's title/content.
  const handleToggleField = async (page, field, value) => {
    setPages((prev) => prev.map((p) => (
      p.page_id === page.page_id ? { ...p, [field]: value } : p
    )));
    try {
      await axios.put(
        `${API}/admin/custom-static-pages/${page.page_id}`,
        { [field]: value },
        { withCredentials: true }
      );
    } catch (error) {
      console.error(`Failed to toggle ${field}:`, error);
      toast.error(error.response?.data?.detail || "Failed to update page");
      setPages((prev) => prev.map((p) => (
        p.page_id === page.page_id ? { ...p, [field]: page[field] } : p
      )));
    }
  };

  const handleCreatePage = () => {
    setEditingPage(null);
    setPageForm({
      title: "",
      slug: "",
      content: "",
      title_style: { ...EMPTY_TITLE_STYLE },
      enabled: true,
      requires_agent_login: false
    });
    setOverlays({ pre_instances: [], post_instances: [] });
    setShowDialog(true);
  };

  const handlePreviewPage = (page) => {
    setPreviewPage(page);
    setShowPreviewModal(true);
  };

  const handleEditPage = async (page) => {
    setEditingPage(page);
    setPageForm({
      title: page.title,
      slug: page.slug,
      content: page.content,
      title_style: { ...EMPTY_TITLE_STYLE, ...(page.title_style || {}) },
      enabled: page.enabled,
      requires_agent_login: page.requires_agent_login || false
    });
    // Load overlays keyed by this page's path
    try {
      const r = await axios.get(`${API}/admin/page-overlays/by-path`, {
        params: { path: `/${page.slug}` },
        withCredentials: true,
      });
      setOverlays({
        pre_instances: r.data?.pre_instances || [],
        post_instances: r.data?.post_instances || [],
      });
    } catch {
      setOverlays({ pre_instances: [], post_instances: [] });
    }
    setShowDialog(true);
  };

  const handleSavePage = async () => {
    if (!pageForm.title.trim()) {
      toast.error("Page title is required");
      return;
    }
    if (!pageForm.slug.trim()) {
      toast.error("Page slug is required");
      return;
    }

    try {
      setSaving(true);

      if (editingPage) {
        await axios.put(
          `${API}/admin/custom-static-pages/${editingPage.page_id}`,
          pageForm,
          { withCredentials: true }
        );
        toast.success("Page updated successfully");
      } else {
        await axios.post(
          `${API}/admin/custom-static-pages`,
          pageForm,
          { withCredentials: true }
        );
        toast.success("Page created successfully");
      }

      // Persist overlays for this page's path. We use the form's slug so a
      // rename + overlay change in the same save still target the right path.
      if (pageForm.slug) {
        try {
          await axios.put(
            `${API}/admin/page-overlays`,
            overlays,
            { params: { path: `/${pageForm.slug}` }, withCredentials: true },
          );
        } catch (e) {
          console.warn("Overlay save failed", e);
        }
      }

      setShowDialog(false);
      fetchPages();
    } catch (error) {
      console.error("Failed to save page:", error);
      toast.error(error.response?.data?.detail || "Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save — enabled only when editing an existing page. New pages still
  // require the explicit "Create Page" click so the admin controls the slug
  // before the first POST fires.
  const autoSaveEnabled = Boolean(showDialog && editingPage);
  const autoSavePayload = { form: pageForm, overlays };
  const autoSave = useAutoSave(
    autoSavePayload,
    async ({ form, overlays: ov }) => {
      await axios.put(
        `${API}/admin/custom-static-pages/${editingPage.page_id}`,
        form,
        { withCredentials: true }
      );
      if (form.slug) {
        await axios.put(
          `${API}/admin/page-overlays`,
          ov,
          { params: { path: `/${form.slug}` }, withCredentials: true }
        );
      }
      // Keep list in sync so the row reflects latest title/slug changes.
      fetchPages({ silent: true });
    },
    {
      enabled: autoSaveEnabled,
      canSave: ({ form }) => Boolean(form?.title?.trim() && form?.slug?.trim()),
    }
  );



  const handleDeletePage = async (pageId) => {
    if (!confirm("Are you sure you want to delete this page? This action cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/custom-static-pages/${pageId}`, {
        withCredentials: true
      });
      toast.success("Page deleted successfully");
      fetchPages();
    } catch (error) {
      console.error("Failed to delete page:", error);
      toast.error(error.response?.data?.detail || "Failed to delete page");
    }
  };

  const handleDuplicatePage = async (page) => {
    try {
      const newTitle = `${page.title} (Copy)`;
      const newSlug = `${page.slug}-copy-${Date.now()}`;
      
      const duplicateData = {
        title: newTitle,
        slug: newSlug,
        content: page.content,
        title_style: page.title_style || {},
        enabled: false,
        requires_agent_login: page.requires_agent_login || false
      };

      await axios.post(`${API}/admin/custom-static-pages`, duplicateData, {
        withCredentials: true
      });

      toast.success(`Page duplicated as "${newTitle}"`);
      fetchPages();
    } catch (error) {
      console.error("Failed to duplicate page:", error);
      toast.error(error.response?.data?.detail || "Failed to duplicate page");
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Custom Static Pages</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage custom pages with rich text content
          </p>
        </div>
        <Button onClick={handleCreatePage}>
          <Plus className="h-4 w-4 mr-2" />
          Create Page
        </Button>
      </div>

      {pages.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No Pages Yet</CardTitle>
            <CardDescription>
              Create your first custom static page to get started
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4">
          {pages.map((page) => (
            <Card key={page.page_id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      {page.title}
                    </CardTitle>
                    <div className="mt-2 flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={page.enabled}
                          onCheckedChange={(checked) => handleToggleField(page, "enabled", checked)}
                          data-testid={`static-page-enabled-toggle-${page.page_id}`}
                          aria-label="Enable page"
                        />
                        <span className="text-xs text-muted-foreground">Enabled</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={Boolean(page.requires_agent_login)}
                          onCheckedChange={(checked) => handleToggleField(page, "requires_agent_login", checked)}
                          data-testid={`static-page-agent-login-toggle-${page.page_id}`}
                          aria-label="Require agent login to view this page"
                        />
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          Agent Only {page.requires_agent_login && <Lock className="h-3 w-3 text-amber-600" />}
                        </span>
                      </div>
                    </div>
                    <CardDescription className="mt-2 flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => handlePreviewPage(page)}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        URL: <code className="bg-muted px-2 py-1 rounded hover:bg-primary/10">/{page.slug}</code>
                      </button>
                      <a
                        href={`${window.location.origin}/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1 text-sm"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span>View Live</span>
                      </a>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditPage(page);
                      }}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicatePage(page);
                      }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePage(page.page_id);
                      }}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? "Edit Page" : "Create New Page"}
            </DialogTitle>
            <DialogDescription>
              Header, navigation and footer are always provided globally — focus on the content of your page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Page Title *</Label>
                <Input
                  placeholder="About Us"
                  value={pageForm.title}
                  onChange={(e) => {
                    setPageForm(prev => ({ ...prev, title: e.target.value }));
                    if (!editingPage) {
                      setPageForm(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                    }
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/</span>
                  <Input
                    placeholder="about-us"
                    value={pageForm.slug}
                    onChange={(e) => setPageForm(prev => ({ ...prev, slug: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Will be accessible at: {window.location.origin}/{pageForm.slug}
                </p>
              </div>
            </div>

            {/* Page Title Style — defaults follow the admin Site Styles.
                Leaving any field blank means "use the site-wide default". */}
            <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <Label className="text-sm font-semibold">Page Title Style</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Defaults follow the admin Site Styles. Set any field below to override it for this page only.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPageForm(prev => ({ ...prev, title_style: { ...EMPTY_TITLE_STYLE } }))}
                  data-testid="reset-title-style-btn"
                >
                  Reset to site default
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Font family</Label>
                  <select
                    value={pageForm.title_style?.fontFamily || ""}
                    onChange={(e) => setPageForm(prev => ({
                      ...prev,
                      title_style: { ...prev.title_style, fontFamily: e.target.value }
                    }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    style={{ fontFamily: pageForm.title_style?.fontFamily || "inherit" }}
                    data-testid="title-style-font-family"
                  >
                    {TITLE_FONTS.map((f) => (
                      <option key={f.value} value={f.value} style={{ fontFamily: f.value || "inherit" }}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Heading size</Label>
                  <HeadingLevelSelect
                    value={pageForm.title_style?.fontSize}
                    onChange={(lvl) => setPageForm(prev => ({
                      ...prev,
                      title_style: { ...prev.title_style, fontSize: lvl }
                    }))}
                    testId="title-style-font-size"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Font weight</Label>
                  <select
                    value={pageForm.title_style?.fontWeight || ""}
                    onChange={(e) => setPageForm(prev => ({
                      ...prev,
                      title_style: { ...prev.title_style, fontWeight: e.target.value }
                    }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="title-style-font-weight"
                  >
                    <option value="">Site default</option>
                    <option value="300">300 — Light</option>
                    <option value="400">400 — Regular</option>
                    <option value="500">500 — Medium</option>
                    <option value="600">600 — Semibold</option>
                    <option value="700">700 — Bold</option>
                    <option value="800">800 — Extrabold</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Colour</Label>
                  <ColorPickerInput
                    value={pageForm.title_style?.color || ""}
                    onChange={(val) => setPageForm(prev => ({
                      ...prev,
                      title_style: { ...prev.title_style, color: val }
                    }))}
                    testId="title-style-color"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Alignment</Label>
                  <select
                    value={pageForm.title_style?.textAlign || ""}
                    onChange={(e) => setPageForm(prev => ({
                      ...prev,
                      title_style: { ...prev.title_style, textAlign: e.target.value }
                    }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="title-style-align"
                  >
                    <option value="">Site default</option>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>

              <div className="rounded-md border border-dashed border-border bg-background p-4">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Live preview</p>
                <div
                  data-testid="title-style-preview"
                  style={{
                    fontFamily: pageForm.title_style?.fontFamily || "var(--font-heading, inherit)",
                    fontSize: resolveFontSize(pageForm.title_style?.fontSize),
                    fontWeight: pageForm.title_style?.fontWeight || "var(--heading-weight, 700)",
                    color: pageForm.title_style?.color || "var(--heading-color, currentColor)",
                    textAlign: pageForm.title_style?.textAlign || "left",
                    lineHeight: "var(--heading-line-height, 1.2)",
                  }}
                >
                  {pageForm.title || "Your page title"}
                </div>
              </div>
            </div>

            <OverlayInstanceSelectors
              pre={overlays.pre_instances}
              post={overlays.post_instances}
              onChange={setOverlays}
            />

            <div className="space-y-2">
              <Label>Page Content *</Label>
              <div className="border rounded-md">
                <TiptapEditor
                  value={pageForm.content}
                  onChange={(content) => setPageForm(prev => ({ ...prev, content }))}
                  placeholder="Start typing your content here..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            {editingPage && (
              <AutoSaveStatus
                status={autoSave.status}
                lastSavedAt={autoSave.lastSavedAt}
                className="mr-auto"
              />
            )}
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {editingPage ? "Close" : "Cancel"}
            </Button>
            {!editingPage && (
              <Button onClick={handleSavePage} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Create Page"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="w-[95vw] sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Preview: {previewPage?.title}</span>
              <a
                href={`${window.location.origin}/${previewPage?.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                <ExternalLink className="h-4 w-4" />
                Open in new tab
              </a>
            </DialogTitle>
            <DialogDescription>
              URL: /{previewPage?.slug}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden border rounded-md bg-white">
            <iframe
              src={`${window.location.origin}/${previewPage?.slug}`}
              className="w-full h-[600px] border-0"
              title="Page Preview"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowPreviewModal(false);
              handleEditPage(previewPage);
            }}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
